import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';

import { SpaceMetadataQuery } from './SpaceMetadataQuery.graphql';
import type {
	SpaceMetadataQueryVariables,
	SpaceMetadataQuery as SpaceMetadataQueryType,
} from './__types__/SpaceMetadataQuery';

/**
 * SSOT payload shared between companyHubAnalyticsHandleEventCaught and linksMenuExtension to create company hub analytic viewed event
 * payload conforms to a particular format required for the analytics service to ingest and publish these view events back to admins on company hub analytics
 */
export const generateViewEventPayload = (
	activationId: string,
	cloudId: string,
	objectId?: string,
	spaceId?: string,
) => ({
	type: 'sendTrackEvent',
	data: {
		action: 'viewed',
		actionSubject: 'companyHubLink',
		source: 'companyHub',
		objectType: 'page',
		objectId,
		attributes: {
			activationId,
			spaceId,
			spaceType: 'system',
			pageType: 'page',
			cloudId,
		},
	},
});

/**
 * SSOT payload shared between companyHubAnalyticsHandleEventCaught and linksMenuExtension to create company hub analytic clicked event
 * payload conforms to a particular format required for the analytics service to ingest and publish these click events back to admins on company hub analytics
 */
export const generateClickEventPayload = (
	activationId: string,
	cloudId: string,
	objectId?: string,
	spaceId?: string,
) => ({
	type: 'sendTrackEvent',
	data: {
		action: 'clicked',
		actionSubject: 'companyHubLink',
		source: 'companyHub',
		objectType: objectId ? 'page' : null,
		objectId,
		attributes: {
			activationId,
			spaceId,
			spaceType: 'system',
			pageType: objectId ? 'page' : null,
			cloudId,
		},
	},
});

/**
 * linking platform analytic events always send the spaceId as destinationContainerId and spaceId as the objectId when the link points to space homepage.
 * However, we need the objectId to always be the page id, otherwise analytics dashboard would not ingest the numbers correctly.
 * if so we return homepageId. if not, we can simply return objectId.
 * destinationContainerId === objectId checks for if the link clicked event was a click on the space homepage, in which case we need to fetch the corresponding pageId
 */
export const getClickAndViewEventObjectIdFromLinkData = async (
	objectId: string,
	destinationContainerId?: string,
) => {
	if (destinationContainerId === objectId) {
		const client = getApolloClient();
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data } = await client.query<SpaceMetadataQueryType, SpaceMetadataQueryVariables>({
			query: SpaceMetadataQuery,
			variables: {
				id: destinationContainerId,
			},
		});
		const homepageId = data?.spaces?.nodes?.[0]?.homepage?.id;
		if (!homepageId) {
			return;
		}
		return homepageId;
	}
	return objectId;
};
